import React from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ERROR_COPY } from "@atlas/shared";
import { FileUp, Trash2 } from "lucide-react";
import {
  analyseRouteShare,
  deleteRouteShare,
  fetchRouteAnalyserAllowance,
  fetchRouteShare,
  RouteAnalyserError,
  type RouteAnalysis,
} from "../api/route-analyser";
import { Button } from "../components/Button";
import type { RouteMapFocus } from "../components/map/RouteAnalysisMap";
import { useAuth } from "../hooks/useAuth";
import { useIsPro } from "../hooks/useIsPro";
import { usePageMeta } from "../hooks/usePageMeta";
import {
  ALLOWANCE_KEY,
  AnalysisResults,
  CopyLinkButtons,
  PersonalStep,
  routeFacts,
  shareUrl,
} from "./RouteAnalyserPage";

// A shared route check (spec 27 round 3), /routes/:id. Public and unlisted:
// anyone with the link sees the generic snapshot taken when it was shared;
// a signed-in viewer can run their own personal check on the stored line.
// Never prerendered, noindex.

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function RouteSharePage() {
  const { id } = useParams({ from: "/_publicApp/routes/$id" });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro } = useIsPro();

  const shareQuery = useQuery({
    queryKey: ["route-share", id],
    queryFn: () => fetchRouteShare(id),
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
  });
  const share = shareQuery.data;

  usePageMeta({
    title: share ? `${share.title}: route check` : "Shared route check",
    description: share
      ? `${routeFacts(share.analysis)}. What this route collects on Atlas, and what it narrowly misses.`
      : "A route checked on Atlas: what it collects, and what it narrowly misses.",
    canonicalPath: `/routes/${id}`,
    noindex: true,
  });

  const allowanceQuery = useQuery({
    queryKey: ALLOWANCE_KEY,
    queryFn: fetchRouteAnalyserAllowance,
    enabled: user != null,
    retry: false,
    staleTime: 60_000,
  });
  const allowance = allowanceQuery.data ?? null;
  const proViewer = allowance ? allowance.tier === "pro" : user != null && (user.isAdmin || isPro);

  const [mine, setMine] = React.useState<RouteAnalysis | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [focus, setFocus] = React.useState<RouteMapFocus | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const personalRun = useMutation({
    mutationFn: () => analyseRouteShare(id),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      setMine(r);
      setFocus(null);
      void qc.invalidateQueries({ queryKey: ALLOWANCE_KEY });
    },
    onError: (e) => setError(e instanceof RouteAnalyserError ? e.message : ERROR_COPY.generic),
  });

  const remove = useMutation({
    mutationFn: () => deleteRouteShare(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["route-share", id] });
      void navigate({ to: "/route-analyser" });
    },
    onError: () => setError("Couldn't stop sharing just now. Try again in a minute."),
  });

  // Pro viewers get their own check straight away, as on the analyser page.
  // Never on a free account's behalf: that would spend their monthly check.
  const autoRan = React.useRef(false);
  React.useEffect(() => {
    if (share && allowance?.tier === "pro" && !autoRan.current) {
      autoRan.current = true;
      personalRun.mutate();
    }
  }, [share, allowance, personalRun]);

  const page: React.CSSProperties = {
    padding: 28,
    display: "flex",
    flexDirection: "column",
    gap: 20,
    flex: 1,
    // See RouteAnalyserPage: signed out the window scrolls, so the sticky map
    // needs this div not to be a scroll box.
    overflow: user ? "auto" : "visible",
    minWidth: 0,
  };

  if (shareQuery.isPending) {
    return (
      <div className="r-page" style={page}>
        <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Loading the route…</div>
      </div>
    );
  }

  if (!share) {
    const gone = shareQuery.error instanceof ApiError && shareQuery.error.status === 404;
    return (
      <div className="r-page" style={page}>
        <div style={{ maxWidth: 560 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Shared route
          </div>
          <h1 style={{ font: "var(--type-h1)", color: "var(--text-primary)", margin: 0 }}>
            {gone ? "This link isn't sharing anything" : "Couldn't load this route"}
          </h1>
          <p style={{ fontSize: 14.5, color: "var(--text-secondary)", lineHeight: 1.55 }}>
            {gone
              ? "Whoever shared it may have stopped sharing, or the link is mistyped. You can still check a route of your own."
              : "Something went wrong on our side. Try again in a minute."}
          </p>
          <Link to="/route-analyser" style={{ textDecoration: "none" }}>
            <Button variant="secondary" size="sm" leftIcon={<FileUp size={14} />}>
              Check a route
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const result = mine ?? share.analysis;
  const url = shareUrl(`/routes/${share.id}`);

  const ownerTools = share.isOwner && (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <CopyLinkButtons url={url} title={share.title} />
      {confirmDelete ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>The link stops working for everyone.</span>
          <Button variant="secondary" size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? "Stopping" : "Stop sharing"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
        </div>
      ) : (
        <div>
          <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setConfirmDelete(true)}>
            Stop sharing
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="r-page" style={page}>
      <div className="r-wrap" style={{ display: "flex", gap: 20, alignItems: "flex-end", justifyContent: "space-between", minWidth: 0 }}>
        <header style={{ minWidth: 0, flex: "1 1 420px" }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Shared route
          </div>
          <h1
            style={{
              font: "var(--type-h1)",
              color: "var(--text-primary)",
              margin: 0,
              letterSpacing: "-.01em",
              overflowWrap: "anywhere",
            }}
          >
            {share.title}
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "8px 0 0", lineHeight: 1.55 }}>
            {share.isOwner ? "You shared this" : share.sharedBy ? `Shared by ${share.sharedBy}` : "Shared on Atlas"}
            {` · checked ${fmtDate(share.createdAt)}`}
          </p>
        </header>
        {ownerTools}
      </div>

      <AnalysisResults
        result={result}
        focus={focus}
        onFocus={(coordinates) => setFocus({ nonce: Date.now(), coordinates })}
        pending={personalRun.isPending}
        top={
          error && (
            <div
              role="alert"
              style={{
                background: "var(--danger-soft)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                padding: "10px 14px",
                fontSize: 13.5,
                color: "var(--text-primary)",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )
        }
        eyebrow={routeFacts(result)}
        beforePanels={
          <PersonalStep
            result={result}
            signedIn={user != null}
            allowance={allowance}
            proViewer={proViewer}
            busy={personalRun.isPending}
            onRequest={() => personalRun.mutate()}
            signInNext={`/routes/${share.id}`}
          />
        }
        mapFooter={
          <Link to="/route-analyser" style={{ textDecoration: "none", alignSelf: "flex-start" }}>
            <Button variant="secondary" size="sm" leftIcon={<FileUp size={14} />}>
              Check a route of your own
            </Button>
          </Link>
        }
        footnote={
          mine
            ? "Checked against your own record just now. Nothing about it is saved to your record."
            : `What the route itself collects, as checked on ${fmtDate(share.createdAt)}. The sharer's own record is never shown.`
        }
      />
    </div>
  );
}
